import React, { useState } from "react";
import tw from "tailwind-styled-components";
import { MdClose } from "react-icons/md";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { CombineInputGroup } from "../Styles/InputStyles";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Images from "../../Images";

const RentingStayRulesModal = ({ onClose }) => {
  const [isEditing, setIsEditing] = useState(false);

  const initialValues = {
    rentalCycle: "7",
    daysInMonth: "22",
    dailyCharges: "500",
    lockInPeriod: "1",
    noticePeriod: "12",
    agreementDuration: "11",
    moveInDate: "28-02-2025",
    moveOutDate: "20-02-2026",
  };

  const validationSchema = Yup.object({
    rentalCycle: Yup.string().required("Required"),
    daysInMonth: Yup.string().required("Required"),
    dailyCharges: Yup.string().required("Required"),
    lockInPeriod: Yup.string().required("Required"),
    noticePeriod: Yup.string().required("Required"),
    agreementDuration: Yup.string().required("Required"),
    moveInDate: Yup.string().required("Required"),
    moveOutDate: Yup.string().required("Required"),
  });

  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };
  const handleSubmit = (values) => {
    console.log("Updated values: ", values);
    setIsEditing(false);
  };

  return (
    <Container>
      <div className="flex items-center justify-between bg-[#0078BD] py-6 px-4">
        <Title className="text-white">
          {isEditing ? "Edit Renting & Stay Rules" : "Renting & Stay Rules"}
        </Title>
        <button onClick={onClose}>
          <MdClose color="white" size={24} />
        </button>
      </div>

      {isEditing ? (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, handleChange, errors, touched }) => (
            <Form className="px-6 h-full flex flex-col justify-between py-4 relative">
              <Wrapper>
                <Section>
                  <SectionTitle>Renting Terms</SectionTitle>
                  <InputGroup>
                    <Label>Rental Cycle (Days)</Label>
                    <Select
                      name="rentalCycle"
                      value={values.rentalCycle}
                      onChange={handleChange}
                      className="w-full pl-2"
                    >
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(
                        (day) => {
                          const ordinalSuffix = getOrdinalSuffix(day);
                          const displayText = `${day}${ordinalSuffix} - ${day}${ordinalSuffix}`;
                          return (
                            <MenuItem key={day} value={day}>
                              {displayText}
                            </MenuItem>
                          );
                        }
                      )}
                    </Select>
                    {touched.rentalCycle && errors.rentalCycle && (
                      <Error>{errors.rentalCycle}</Error>
                    )}
                  </InputGroup>
                </Section>

                <Section>
                  <SectionTitle>Stay Terms</SectionTitle>
                  <CombineInputGroup>
                    <InputGroup>
                      <Label>Days In Month</Label>
                      <Select
                        name="daysInMonth"
                        value={values.daysInMonth}
                        onChange={handleChange}
                        className="w-full pl-2"
                      >
                        {[...Array(31).keys()].map((num) => {
                          const value = (num + 1) * 1;
                          return (
                            <MenuItem key={value} value={value}>
                              {value}
                            </MenuItem>
                          );
                        })}
                      </Select>
                      {touched.daysInMonth && errors.daysInMonth && (
                        <Error>{errors.daysInMonth}</Error>
                      )}
                    </InputGroup>

                    <InputGroup>
                      <Label>Daily Charges (₹)</Label>
                      <Input
                        type="text"
                        name="dailyCharges"
                        value={values.dailyCharges}
                        onChange={handleChange}
                      />
                      {touched.dailyCharges && errors.dailyCharges && (
                        <Error>{errors.dailyCharges}</Error>
                      )}
                    </InputGroup>
                  </CombineInputGroup>

                  <CombineInputGroup>
                    <InputGroup>
                      <Label>Lock-in Period (Months)</Label>
                      <Select
                        name="lockInPeriod"
                        value={values.lockInPeriod}
                        onChange={handleChange}
                        className="w-full pl-2"
                      >
                        {[...Array(24).keys()].map((num) => {
                          const value = (num + 1) * 0.5;
                          return (
                            <MenuItem key={value} value={value}>
                              {value}
                            </MenuItem>
                          );
                        })}
                      </Select>
                      {touched.lockInPeriod && errors.lockInPeriod && (
                        <Error>{errors.lockInPeriod}</Error>
                      )}
                    </InputGroup>

                    <InputGroup>
                      <Label>Notice Period (Months)</Label>
                      <Select
                        name="noticePeriod"
                        value={values.noticePeriod}
                        onChange={handleChange}
                        className="w-full pl-2"
                      >
                        {[...Array(24).keys()].map((num) => {
                          const value = (num + 1) * 0.5;
                          return (
                            <MenuItem key={value} value={value}>
                              {value}
                            </MenuItem>
                          );
                        })}
                      </Select>
                      {touched.noticePeriod && errors.noticePeriod && (
                        <Error>{errors.noticePeriod}</Error>
                      )}
                    </InputGroup>
                  </CombineInputGroup>

                  <InputGroup>
                    <Label>Agreement Duration (Months)</Label>
                    <Select
                      name="agreementDuration"
                      value={values.agreementDuration}
                      onChange={handleChange}
                      className="w-full pl-2"
                    >
                      {[...Array(24).keys()].map((num) => {
                        const value = (num + 1) * 0.5;
                        return (
                          <MenuItem key={value} value={value}>
                            {value}
                          </MenuItem>
                        );
                      })}
                    </Select>
                    {touched.agreementDuration && errors.agreementDuration && (
                      <Error>{errors.agreementDuration}</Error>
                    )}
                  </InputGroup>

                  <CombineInputGroup>
                    <InputGroup>
                      <Label>Moved In</Label>
                      <FieldWrapper className="relative">
                        <Input
                          type="date"
                          name="moveInDate"
                          id="moveInDate"
                          className="pl-7"
                          value={values.moveInDate}
                          onChange={handleChange}
                        />
                        {touched.moveInDate && errors.moveInDate && (
                          <Error>{errors.moveInDate}</Error>
                        )}
                        <img
                          src={Images.Calender}
                          alt="Calendar icon"
                          className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 cursor-pointer"
                        />
                      </FieldWrapper>
                    </InputGroup>

                    <InputGroup>
                      <Label>Moved Out</Label>
                      <FieldWrapper className="relative">
                        <Input
                          type="date"
                          name="moveOutDate"
                          id="moveOutDate"
                          className="pl-7"
                          value={values.moveOutDate}
                          onChange={handleChange}
                        />
                        {touched.moveOutDate && errors.moveOutDate && (
                          <Error>{errors.moveOutDate}</Error>
                        )}
                        <img
                          src={Images.Calender}
                          alt="Calendar icon"
                          className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 cursor-pointer"
                        />
                      </FieldWrapper>
                    </InputGroup>
                  </CombineInputGroup>
                </Section>
              </Wrapper>

              <BtnWrapper $isEditing={isEditing}>
                <SecondaryButton
                  $isEditing={isEditing}
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </SecondaryButton>
                <PrimaryButton $isEditing={isEditing} type="submit">
                  Update
                </PrimaryButton>
              </BtnWrapper>
            </Form>
          )}
        </Formik>
      ) : (
        <div className="px-6 h-full flex flex-col justify-between py-4 relative">
          <Wrapper>
            <Section>
              <SectionTitle>Renting Terms</SectionTitle>
              <Row>
                <Label>Rent Addition Cycle</Label>
                <Value>{initialValues.rentalCycle}</Value>
              </Row>
            </Section>

            <Section>
              <SectionTitle>Stay Terms</SectionTitle>
              <Row>
                <Label>Days In Month</Label>
                <Value>{initialValues.daysInMonth}</Value>
              </Row>
              <Row>
                <Label>Daily Charges</Label>
                <Value>{initialValues.dailyCharges}</Value>
              </Row>
              <Row>
                <Label>Lock-in Period</Label>
                <Value>{initialValues.lockInPeriod}</Value>
              </Row>
              <Row>
                <Label>Notice Period</Label>
                <Value>{initialValues.noticePeriod}</Value>
              </Row>
              <Row>
                <Label>Agreement Duration</Label>
                <Value>{initialValues.agreementDuration}</Value>
              </Row>
              <Row>
                <Label>Moved In</Label>
                <Value>{initialValues.moveInDate}</Value>
              </Row>
              <Row>
                <Label>Moved Out</Label>
                <Value>{initialValues.moveOutDate}</Value>
              </Row>
            </Section>
          </Wrapper>

          <BtnWrapper $isEditing={isEditing}>
            <SecondaryButton $isEditing={isEditing}>
              Copy settings
            </SecondaryButton>
            <PrimaryButton
              $isEditing={isEditing}
              onClick={() => setIsEditing(true)}
            >
              Edit
            </PrimaryButton>
          </BtnWrapper>
        </div>
      )}
    </Container>
  );
};

const Wrapper = tw.div`grid gap-2 my-2`;
const Section = tw.div``;
const FieldWrapper = tw.div`w-full`;
const Container = tw.div`bg-white w-full h-full min-h-[100vh] flex flex-col`;
const Title = tw.h1`text-lg md:text-xl font-medium text-center text-gray-800`;
const SectionTitle = tw.h2`text-lg font-semibold text-gray-700 mb-2`;
const Row = tw.div`flex justify-between p-2`;
const Label = tw.label`text-sm text-gray-500 font-normal`;
const Value = tw.span`text-gray-800 font-medium`;
const InputGroup = tw.div`w-full flex flex-col space-y-1.5 mb-3`;
const Input = tw.input`border border-gray-300 rounded h-10 px-2 py-1 focus:ring-1 focus:ring-primary-color focus:border-primary-color focus:outline-none`;
const Error = tw.p`text-rose-600 text-sm`;

const BtnWrapper = tw.div`${({ $isEditing }) =>
  $isEditing
    ? "flex gap-3"
    : "flex flex-col sm:flex-row gap-3 w-full sm:justify-between mt-4"}`;

const PrimaryButton = tw.button`${({ $isEditing }) =>
  $isEditing
    ? "bg-primary-color w-1/2 text-white px-4 py-2 rounded-lg hover:bg-[#65adf0]"
    : "bg-primary-color w-full sm:w-1/2 text-white px-4 py-2 rounded-lg hover:bg-[#65adf0]"}`;

const SecondaryButton = tw.button`${({ $isEditing }) =>
  $isEditing
    ? "bg-white border w-1/2 border-primary-color text-primary-color font-medium px-4 py-1 rounded-lg"
    : "bg-white border w-full sm:w-1/2 border-primary-color text-primary-color font-medium px-4 py-1 rounded-lg"}`;

export default RentingStayRulesModal;
